
import { DescriptionAndTags, NameInput } from "components/actions/forms";
import { LoadingSpinner } from "components/loading-spinner/LoadingSpinner";
import { defaultImportMap, importmap } from "components/playground/PlaygroundLoader";
import debounce from "lodash.debounce";
import { useEffect, useState } from "react";
import { FieldValues, useForm, Validate } from "react-hook-form";
import { packageRegistryApi } from "services/package-registry-service/PackageRegistryService";
import { sandboxApi } from "services/sandbox-api/api";
import { sandboxClient } from "services/sandbox-api/sandboxClient";
import { sourceFilesTransformBack } from "services/sandbox-api/transforms";
import { AttributeSerializer } from "services/share-service/serializers/AttributeSerializer";
import { CodeShare, CodeShareMetadata, CodeShareUser } from "types";
import { SvgArrowRight } from "@itwin/itwinui-icons-react";
import { Alert, Button, Code, Dialog, Icon, Surface, Text } from "@itwin/itwinui-react";
import { SourceFile } from "@sandbox/code-editor";
import { FormSpinner } from "../FormSpinner";
import { migrationV2toV4, migrationV3toV4 } from "./migration-docs";
import styles from "./MigrationDialog.module.css";

interface ForkSandboxDialogProps {
  userId: string;
  sandboxId: string;
  sandboxUser: CodeShareUser;
  onConfirm: (sandbox: CodeShareMetadata) => void;
  onClose: () => void;
}

interface FormData extends FieldValues {
  currentName?: string;
  currentDescription?: string;
  updatedName?: string;
  updatedtDescription?: string;
};

export const MigrationDialog = ({ userId, sandboxId, sandboxUser, onConfirm, onClose }: ForkSandboxDialogProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [updatingSandbox, setUpdatingSandbox] = useState(false);
  const [loadingDialog, setLoadingDialog] = useState(true);
  const [sandbox, setSandbox] = useState<CodeShare>();
  const [sandboxTemplate, setSandboxTemplate] = useState<CodeShare>();
  const [getSandbox] = sandboxApi.useLazyCodeShareQuery();
  const [getSandboxByName] = sandboxApi.useLazyCodeShareResolveQuery();
  const [updateSandboxFiles] = sandboxApi.useCodeShareUpdateFilesMutation();
  const [updateSandbox] = sandboxApi.useCodeShareUpdateMutation();
  const [getImportmap] = packageRegistryApi.useLazyGetImportmapQuery();
  const [currentVersion, setCurrentVersion] = useState<string>();
  const [updatedVersion, setUpdatedVersion] = useState<string>();
  const [forkSandbox] = sandboxApi.useCodeShareForkMutation();
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors }
  } = useForm<FormData>();

  const onSubmit = async (fields: FormData) => {
    if (sandbox && sandboxTemplate) {
      setUpdatingSandbox(true);

      try {
        if (control.getFieldState("currentName").isDirty || control.getFieldState("currentDescription").isDirty) {
          await updateSandbox({
            id: sandbox.id,
            userId: sandbox.userId,
            name: fields.currentName,
            description: fields.currentDescription
          });
        };

        const forkedSandox = await forkSandbox({
          forkCodeshareId: sandbox.id,
          forkUserId: sandbox.userId,
          name: fields.updatedName!,
          description: fields.updatedtDescription,
          attributes: AttributeSerializer.serialize(sandboxTemplate.attributes),
          modules: sandboxTemplate.modules,
        }).unwrap();

        const files: SourceFile[] = [...forkedSandox.files, { filePath: "/migration.md", content: sandbox.itwinVersion !== "3.x" ? migrationV2toV4 : migrationV3toV4, type: "file" }];
        await updateSandboxFiles({
          id: forkedSandox.id,
          userId: forkedSandox.userId,
          files: sourceFilesTransformBack(files)
        }).unwrap();

        setUpdatingSandbox(false);
        onConfirm(forkedSandox);

      } catch {
        setUpdatingSandbox(false);
      }
      finally {
        setTimeout(onClose, 200);
      }
    }
  }

  const onCancel = () => {
    setIsModalOpen(false);
    setTimeout(onClose, 200);
  };

  const getVersion = async (sandbox: CodeShare) => {

    let resolvedImportmap = sandbox.attributes.importmap as string;

    if (!resolvedImportmap) {
      if (sandbox.itwinVersion === "4.x") {
        resolvedImportmap = importmap["itwin-v4-importmap-v1"];
      }
      else if (sandbox.itwinVersion === "3.x") {
        resolvedImportmap = importmap["itwin-v3-importmap-v1"];
      }
      else if (sandbox.itwinVersion === "2.x") {
        resolvedImportmap = importmap["itwin-v2-importmap-v1"];
      } else {
        resolvedImportmap = importmap[defaultImportMap];
      }
    } else {
      resolvedImportmap = importmap[resolvedImportmap] ?? importmap[defaultImportMap];
    }

    const data = await getImportmap(resolvedImportmap).unwrap();
    const pkg = data.packages.find((item) => item.name === "@itwin/core-bentley");
    return pkg?.version || sandbox.itwinVersion!;
  };

  const nameValidation: Validate<string, FormData> = (value, values) => {
    // current name was changed, updated name remains the same - all good
    if (values.currentName !== sandbox?.name && value === sandbox?.name)
      return true;

    return new Promise((resolve) => {
      debounce(async () => {
        const result = await sandboxClient.sandboxNameExists({ userName: sandbox?.username!, sandboxName: value });
        resolve(result === true ? "This sandbox name already exists" : undefined);
      }, 500, { leading: true })();
    });
  }

  const currentNameValidation: Validate<string, FormData> = (value, values) => {
    // current name was changed, updated name remains the same - all good
    if (values.currentName === sandbox?.name)
      return true;

    return new Promise((resolve) => {
      debounce(async () => {
        const result = await sandboxClient.sandboxNameExists({ userName: sandbox?.username!, sandboxName: value });
        resolve(result === true ? "This sandbox name already exists" : undefined);
      }, 500, { leading: true })();
    });
  }

  useEffect(() => {
    setTimeout(() => setIsModalOpen(true), 5);
    getSandbox({ userId, codeshareId: sandboxId }, true).unwrap().then((data) => setSandbox(data));
    getSandboxByName({ userName: "iTwinPlatform", sandboxName: "Basic Viewport Template" }, true).unwrap().then((data) => setSandboxTemplate(data));
  }, []);

  useEffect(() => {
    if (sandbox && sandboxTemplate) {
      reset({
        currentName: `${sandbox.name}`,
        currentDescription: sandbox.description,
        updatedName: `${sandbox.name}`,
        updatedDescription: sandbox.description,
      });

      const p1 = getVersion(sandbox);
      const p2 = getVersion(sandboxTemplate);
      Promise.all([p1, p2]).then(([v1, v2]) => {
        setCurrentVersion(v1);
        setUpdatedVersion(v2);
        setLoadingDialog(false);
      });
    }
  }, [sandbox?.id, sandboxTemplate?.id]);

  return (
    <Dialog isOpen={isModalOpen} onClose={onCancel} closeOnEsc closeOnExternalClick preventDocumentScroll trapFocus setFocus isDismissible>
      <Dialog.Backdrop />
      <Dialog.Main data-testid="atp-migrate-sandbox-dialog" className={styles.dialog}>
        <Dialog.TitleBar titleText="Sandbox Update" />
        <form onSubmit={handleSubmit(onSubmit)} noValidate autoComplete="off">
          <Dialog.Content className={styles.layout}>
            {loadingDialog
              ? <LoadingSpinner centered message="Loading..." />
              : <>
                <Text variant="body">
                  Change the following properties to make changes to both current Sandbox and updated Sandbox
                </Text>
                <div>
                  <div className={styles.caption}>
                    <Text variant="leading" isMuted style={{ textAlign: "center" }}>Current Sandbox</Text>
                    <Icon size="large"><SvgArrowRight /></Icon>
                    <Text variant="leading" isMuted style={{ textAlign: "center" }}>Updated Sandbox</Text>
                  </div>
                  <div className={styles.compare}>
                    <div className={styles.formFields}>
                      <Surface elevation={4} className={styles.formFields}>
                        <NameInput name="currentName" control={control} userName={sandboxUser.username} validate={currentNameValidation} />
                        <DescriptionAndTags name="currentDescription" control={control} descriptionOnly={true} />
                        <Text isMuted>iTwin.js core packages: <span className={styles.oldVersion}>{currentVersion}</span></Text>
                      </Surface>
                    </div>
                    <div className={styles.formFields}>
                      <Surface elevation={4} className={styles.formFields}>
                        <NameInput name="updatedName" control={control} userName={sandboxUser.username} validate={nameValidation} />
                        <DescriptionAndTags name="updatedDescription" control={control} descriptionOnly={true} />
                        <Text isMuted>iTwin.js core packages: <span className={styles.newVersion}>{updatedVersion}</span></Text>
                      </Surface>
                    </div >
                  </div>
                </div>
                <Alert type="informational">
                  The update process creates a <b>fork</b> of this sandbox with all dependencies updated to the latest version.
                  Source files will not be modified, please review <Code>migration.md</Code> file added to the updated sandbox for further instructions.
                </Alert>
              </>}
          </Dialog.Content>
          <Dialog.ButtonBar>
            {updatingSandbox && <FormSpinner message="Saving..." />}
            <Button styleType="high-visibility" type="submit" disabled={Object.keys(errors).length > 0} data-testid="atp-migrate-btn">Update</Button>
            <Button onClick={onCancel}>Cancel</Button>
          </Dialog.ButtonBar>
        </form>
      </Dialog.Main>
    </Dialog>
  )
};
